import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack, CheckCircle, Print, Edit as EditIcon, Save, Cancel, Delete } from '@mui/icons-material';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { formatCurrency } from '../components/CurrencyTextField';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import { DEFAULT_PAYMENT_TERMS } from '../constants/defaultPaymentTerms';
import { fetchPaymentTermsOptions } from '../services/paymentTermsService';
import { formatDateForInput, formatDateOnly, parseLocalDate } from '../utils/dateUtils';
import { formatQuantityDisplay } from '../utils/quantityDisplay';

function getTermsDays(terms) {
  if (!terms) return 0;
  const m = terms.match(/^Net\s+(\d+)$/i);
  if (m) return parseInt(m[1], 10) || 0;
  if (/^(Payable\s+On\s+Receipt|Pay\s+before\s+ship|Net\s*0)$/i.test(terms)) return 0;
  return 0;
}

function addDays(dateStr, days) {
  const d = parseLocalDate(dateStr);
  if (!d) return '';
  d.setDate(d.getDate() + (days || 0));
  return formatDateForInput(d);
}

const CustomerInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markPaidConfirmOpen, setMarkPaidConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingTerms, setEditingTerms] = useState(false);
  const [editPaymentTerms, setEditPaymentTerms] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [savingTerms, setSavingTerms] = useState(false);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState(DEFAULT_PAYMENT_TERMS);
  const [editingAmounts, setEditingAmounts] = useState(false);
  const [editShipping, setEditShipping] = useState('');
  const [editTax, setEditTax] = useState('');
  const [savingAmounts, setSavingAmounts] = useState(false);

  useEffect(() => {
    loadInvoice();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetchPaymentTermsOptions().then((list) => {
      if (!cancelled) setPaymentTermsOptions(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const invoiceTermsSelectOptions = useMemo(() => {
    const base = paymentTermsOptions;
    const t = editPaymentTerms;
    if (t && !base.includes(t)) {
      return [...base, t];
    }
    return base;
  }, [paymentTermsOptions, editPaymentTerms]);

  const canEditInvoiceAmounts =
    invoice &&
    !invoice.paid_at &&
    parseFloat(invoice.amount_paid || 0) === 0;

  const startEditTerms = () => {
    const terms = invoice.payment_terms || 'Payable On Receipt';
    setEditPaymentTerms(terms);
    setEditDueDate(formatDateForInput(invoice.due_date));
    setEditingTerms(true);
  };

  const startEditAmounts = () => {
    setEditShipping(invoice.shipping_amount != null ? String(invoice.shipping_amount) : '');
    setEditTax(invoice.tax_amount != null ? String(invoice.tax_amount) : '');
    setEditingAmounts(true);
  };

  const handleSaveAmounts = async () => {
    if (!invoice?.id) return;
    try {
      setSavingAmounts(true);
      setError('');
      const ship = editShipping === '' ? 0 : parseFloat(editShipping);
      const tax = editTax === '' ? 0 : parseFloat(editTax);
      if (!Number.isFinite(ship) || ship < 0 || !Number.isFinite(tax) || tax < 0) {
        setError('Shipping and tax must be valid numbers ≥ 0.');
        return;
      }
      await customerInvoiceService.update(invoice.id, {
        shipping_amount: ship,
        tax_amount: tax
      });
      setEditingAmounts(false);
      loadInvoice();
    } catch (err) {
      console.error('Error updating invoice amounts:', err);
      setError(err.response?.data?.message || 'Failed to update shipping and tax');
    } finally {
      setSavingAmounts(false);
    }
  };

  const handleTermsChange = (newTerms) => {
    setEditPaymentTerms(newTerms);
    const days = getTermsDays(newTerms);
    if (invoice?.invoice_date && days >= 0) {
      setEditDueDate(addDays(invoice.invoice_date, days));
    }
  };

  const handleSaveTerms = async () => {
    if (!invoice?.id) return;
    try {
      setSavingTerms(true);
      setError('');
      const termsDays = getTermsDays(editPaymentTerms);
      await customerInvoiceService.update(invoice.id, {
        payment_terms: editPaymentTerms,
        due_date: editDueDate || null,
        terms_days: termsDays
      });
      setEditingTerms(false);
      loadInvoice();
    } catch (err) {
      console.error('Error updating terms:', err);
      setError(err.response?.data?.message || 'Failed to update terms');
    } finally {
      setSavingTerms(false);
    }
  };

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await customerInvoiceService.getById(id);
      setInvoice(response.data);
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError(err.response?.data?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    try {
      setMarkingPaid(true);
      setError('');
      await customerInvoiceService.markPaid(id);
      setMarkPaidConfirmOpen(false);
      loadInvoice();
    } catch (err) {
      console.error('Error marking paid:', err);
      setError(err.response?.data?.message || 'Failed to mark invoice as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDeleteInvoice = async () => {
    try {
      setDeleting(true);
      setError('');
      await customerInvoiceService.delete(id);
      setDeleteConfirmOpen(false);
      if (invoice?.work_order_id) {
        navigate(`/work-orders/${invoice.work_order_id}`, { replace: true });
      } else {
        navigate('/customer-invoices', { replace: true });
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = { Due: 'info', 'Past Due': 'error', Paid: 'success' };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    return formatDateOnly(dateString, 'MM/dd/yyyy') || '—';
  };

  const customerName =
    invoice?.company_name ||
    [invoice?.customer_first_name, invoice?.customer_last_name].filter(Boolean).join(' ') ||
    '—';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Alert severity="error">Invoice not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customer-invoices')} sx={{ mt: 2 }}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Print-friendly invoice view */}
      <PrintPage title="Invoice">
        <div className="print-quote-recipient" style={{ marginBottom: 16 }}>
          <div className="print-quote-to-labels">
            <strong>Bill</strong>
            <strong>To</strong>
          </div>
          <div className="print-quote-to-address">
            <div>{customerName}</div>
            {invoice.customer_address_line1 && <div>{invoice.customer_address_line1}</div>}
            {invoice.customer_address_line2 && <div>{invoice.customer_address_line2}</div>}
            {(invoice.customer_city || invoice.customer_state || invoice.customer_zip_code) && (
              <div>
                {invoice.customer_city ? `${invoice.customer_city}, ` : ''}
                {invoice.customer_state || ''} {invoice.customer_zip_code || ''}
              </div>
            )}
          </div>
        </div>

        <div className="print-quote-details-box" style={{ marginBottom: 16 }}>
          <div className="print-quote-details-left">
            <div><strong>Invoice #:</strong> {invoice.identifier ?? invoice.invoice_number}</div>
            <div>
              <strong>Invoice Date:</strong>{' '}
              {formatDate(invoice.invoice_date)}
            </div>
            {invoice.due_date && (
              <div>
                <strong>Due Date:</strong> {formatDate(invoice.due_date)}
              </div>
            )}
          </div>
          {invoice.packing_slip_id ? (
            <div className="print-quote-details-center">
              <div>
                <strong>Shipment No.</strong>{' '}
                {invoice.packing_slip_identifier ?? invoice.packing_slip_number ?? `#${invoice.packing_slip_id}`}
              </div>
              <div>
                <strong>Shipped On:</strong>{' '}
                {invoice.packing_slip_shipped_date ? formatDate(invoice.packing_slip_shipped_date) : '—'}
              </div>
            </div>
          ) : null}
          <div className="print-quote-details-right">
            <div><strong>Terms:</strong> {invoice.payment_terms || 'Payable On Receipt'}</div>
            <div><strong>Status:</strong> {invoice.status || 'open'}</div>
          </div>
        </div>

        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
          <thead>
            <tr>
              <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Item</th>
              <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Description</th>
              <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Qty</th>
              <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Unit Price</th>
              <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Line Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).length > 0 ? (
              (invoice.items || []).map((item) => (
                <tr key={item.id}>
                  <td style={{ padding: '4px 6px', fontSize: '11px' }}>
                    {item.item_type === 'service' ? effectiveServiceCode(item) || '—' : item.part_number || '—'}
                  </td>
                  <td style={{ padding: '4px 6px', fontSize: '11px' }}>{item.description || item.part_name || item.service_name || '—'}</td>
                  <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity ?? 0)}</td>
                  <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(item.unit_price, '—')}</td>
                  <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(item.line_total, '—')}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'center' }}>No line items</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="print-quote-total-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Subtotal</span>
            <span>{formatCurrency(invoice.subtotal, '—')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Shipping</span>
            <span>{formatCurrency(invoice.shipping_amount, '—')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Tax</span>
            <span>{formatCurrency(invoice.tax_amount, '—')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 8, paddingTop: 8, borderTop: '1px solid #ccc' }}>
            <strong>Total</strong>
            <span>{formatCurrency(invoice.total_amount, '—')}</span>
          </div>
        </div>
      </PrintPage>
      <Box className="no-print">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate('/customer-invoices')} title="Back">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Invoice {invoice.identifier ?? invoice.invoice_number}
          </Typography>
          <Chip
            label={invoice.status}
            color={getStatusColor(invoice.status)}
            size="small"
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {(invoice.balance_due ?? (parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0))) > 0 && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckCircle />}
              onClick={() => setMarkPaidConfirmOpen(true)}
            >
              Mark Paid
            </Button>
          )}
          <IconButton size="small" onClick={() => printWithFilename(invoice?.identifier || invoice?.invoice_number || 'Invoice')} title="Print">
            <Print />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteConfirmOpen(true)}
            title="Delete invoice"
            disabled={deleting}
          >
            <Delete />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {invoice.identifier ?? invoice.invoice_number ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {invoice.customer_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${invoice.customer_id}`)}
                >
                  {customerName}
                </Button>
              ) : (
                customerName
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order #</Typography>
            <Typography variant="body2">
              {invoice.work_order_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/work-orders/${invoice.work_order_id}`)}
                >
                  {invoice.work_order_identifier ?? invoice.work_order_number ?? `#${invoice.work_order_id}`}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
            <Typography variant="body2">{invoice.po_number || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slip #</Typography>
            <Typography variant="body2">
              {invoice.packing_slip_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/packing-slips/${invoice.packing_slip_id}`)}
                >
                  {invoice.packing_slip_identifier ?? invoice.packing_slip_number ?? `#${invoice.packing_slip_id}`}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice Date</Typography>
            <Typography variant="body2">{formatDate(invoice.invoice_date)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Due Date</Typography>
            <Typography variant="body2">{formatDate(invoice.due_date)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(invoice.total_amount, '—')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            <Chip
              label={invoice.status ?? '—'}
              color={getStatusColor(invoice.status)}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Invoice Details
                </Typography>
                {!editingTerms ? (
                  <Button size="small" startIcon={<EditIcon />} onClick={startEditTerms}>
                    Edit terms & due date
                  </Button>
                ) : null}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Invoice #
                  </Typography>
                  <Typography variant="body2">{invoice.identifier ?? invoice.invoice_number}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Invoice Date
                  </Typography>
                  <Typography variant="body2">{formatDate(invoice.invoice_date)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Due Date
                  </Typography>
                  {editingTerms ? (
                    <TextField
                      size="small"
                      type="date"
                      value={editDueDate}
                      onChange={(e) => setEditDueDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'aria-label': 'Due date' }}
                    />
                  ) : (
                    <Typography variant="body2">{formatDate(invoice.due_date)}</Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Terms
                  </Typography>
                  {editingTerms ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={editPaymentTerms}
                        onChange={(e) => handleTermsChange(e.target.value)}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Payment terms' }}
                      >
                        {invoiceTermsSelectOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2">{invoice.payment_terms || '—'}</Typography>
                  )}
                </Grid>
                {editingTerms && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" startIcon={<Save />} onClick={handleSaveTerms} disabled={savingTerms}>
                        {savingTerms ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="small" startIcon={<Cancel />} onClick={() => setEditingTerms(false)} disabled={savingTerms}>
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body2">{customerName}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/work-orders/${invoice.work_order_id}`)}
                  >
                    View Order #{invoice.work_order_identifier ?? invoice.work_order_number}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Line Items
              </Typography>
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableCell-root': { py: 0.5, px: 0.75, fontSize: '0.8125rem' },
                    '& .MuiTableCell-head': { py: 0.5, fontSize: '0.75rem' }
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Unit Price
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Line Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(invoice.items || []).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.item_type === 'service' ? 'Service' : 'Part'}</TableCell>
                        <TableCell>
                          {item.item_type === 'service' ? effectiveServiceCode(item) || '—' : item.part_number || '—'}
                        </TableCell>
                        <TableCell>{item.description || item.part_name || item.service_name || '—'}</TableCell>
                        <TableCell align="right">{formatQuantityDisplay(item.quantity ?? 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price, '—')}</TableCell>
                        <TableCell align="right">{formatCurrency(item.line_total, '—')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Totals
                </Typography>
                {!editingAmounts && canEditInvoiceAmounts ? (
                  <Button size="small" startIcon={<EditIcon />} onClick={startEditAmounts}>
                    Edit shipping &amp; tax
                  </Button>
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">{formatCurrency(invoice.subtotal, '—')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                <Typography variant="body2">Shipping</Typography>
                {editingAmounts ? (
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: '0.01', 'aria-label': 'Shipping amount' }}
                    value={editShipping}
                    onChange={(e) => setEditShipping(e.target.value)}
                    sx={{ width: 120 }}
                  />
                ) : (
                  <Typography variant="body2">{formatCurrency(invoice.shipping_amount, '—')}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                <Typography variant="body2">Tax</Typography>
                {editingAmounts ? (
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: '0.01', 'aria-label': 'Tax amount' }}
                    value={editTax}
                    onChange={(e) => setEditTax(e.target.value)}
                    sx={{ width: 120 }}
                  />
                ) : (
                  <Typography variant="body2">{formatCurrency(invoice.tax_amount, '—')}</Typography>
                )}
              </Box>
              {editingAmounts && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="contained" startIcon={<Save />} onClick={handleSaveAmounts} disabled={savingAmounts}>
                    {savingAmounts ? 'Saving...' : 'Save amounts'}
                  </Button>
                  <Button size="small" startIcon={<Cancel />} onClick={() => setEditingAmounts(false)} disabled={savingAmounts}>
                    Cancel
                  </Button>
                </Box>
              )}
              {!canEditInvoiceAmounts && (invoice.paid_at || parseFloat(invoice.amount_paid || 0) > 0) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Shipping and tax cannot be changed after payment is recorded.
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 1.5,
                  pt: 1,
                  borderTop: 1,
                  borderColor: 'divider'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Total
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(invoice.total_amount, '—')}
                </Typography>
              </Box>
              {(invoice.amount_paid || 0) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Amount paid: {formatCurrency(invoice.amount_paid)}
                  </Typography>
                  {(invoice.balance_due ?? parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0)) > 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                      Balance due: {formatCurrency(invoice.balance_due ?? (parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0)))}
                    </Typography>
                  )}
                </Box>
              )}
              {invoice.paid_at && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                  Paid on {formatDate(invoice.paid_at)}
                </Typography>
              )}
              {(invoice.payments_applied || []).length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Payments applied
                  </Typography>
                  {(invoice.payments_applied || []).map((p) => (
                    <Typography key={p.id} variant="caption" sx={{ display: 'block' }}>
                      {p.payment_number} ({formatDate(p.payment_date)}): {formatCurrency(p.amount_applied)}
                    </Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>

      <Dialog open={markPaidConfirmOpen} onClose={() => setMarkPaidConfirmOpen(false)}>
        <DialogTitle>Mark Invoice as Paid</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark invoice {invoice.identifier ?? invoice.invoice_number} as paid?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidConfirmOpen(false)} disabled={markingPaid}>
            Cancel
          </Button>
          <Button onClick={handleMarkPaid} color="success" variant="contained" disabled={markingPaid}>
            {markingPaid ? 'Saving...' : 'Mark Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete invoice {invoice.identifier ?? invoice.invoice_number}? Packing slips are not deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteInvoice} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerInvoiceDetail;
